// src/pages/TicketDetail.js — ticket conversation (redesign 2026-10-23).
// Oldest → newest like an email thread, with the original request on top; older
// messages collapse to one-line rows; quoted history and signatures fold away; the
// rail stays out of the way (Layout collapses it on this page). Every action the old
// page had is still here: header actions, the details panel and the composer.
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { getTicket, updateTicket, addResponse, suggestResponse, getAgents, getTicketComments, scheduleTicket, unscheduleTicket, relaunchFtr } from "../api";
import { ArrowLeft, ChevronLeft, ChevronRight, ChevronDown, Sparkles, Send, Lock, Code, Wrench, X, Plus, Paperclip, Building2, CalendarClock, Bot, Monitor, MoreHorizontal, Search, Bold, MessageSquare, Users, History, Eye } from "lucide-react";
import RemoteButton from "../components/RemoteButton";
import SecureLinkModal from "../components/SecureLinkModal";
import { useAuth } from "../context/AuthContext";
import { MiniAvatar } from "../theme";
import useIsMobile from "../hooks/useIsMobile";
import TimeTracker from "../components/TimeTracker";
import TagPill from "../components/TagPill";
import CcSuggest from "../components/CcSuggest";
import { filesFromPaste, addWithinLimits, fmtFileSize } from "../utils/pastedFiles";
import {
  ConvoStyles, Timeline, Chips, KeyHints, JumpLatest, Avatar, Sel, PillSel, TopResizeHandle,
  STATUS_TONE, PRIORITY_TONE, previewText, stripHtml, fmtFull, isTypingTarget, splitBody,
} from "../components/conversation/Conversation";

const API   = (process.env.REACT_APP_API_URL || "https://ticketing.midtc.com") + "/api";
const token = () => localStorage.getItem("midtc_token") || "";

// ─── Send to Dev Modal ────────────────────────────────────────
function SendToDevModal({ ticket, agents, onClose, onSent }) {
  const [selectedDevs, setSelectedDevs] = useState([]);
  const [notes,        setNotes]        = useState("");
  const [priority,     setPriority]     = useState(ticket.priority);
  const [sending,      setSending]      = useState(false);
  const [error,        setError]        = useState("");

  const toggleDev = (id) => setSelectedDevs(p => p.includes(id) ? p.filter(d=>d!==id) : [...p, id]);

  const handleSend = async () => {
    if (!selectedDevs.length) { setError("Select at least one developer."); return; }
    setSending(true); setError("");
    try {
      const res  = await fetch(`${API}/tickets/${ticket.id}/send-to-dev`, {
        method:  "POST",
        headers: { "Content-Type":"application/json", Authorization:`Bearer ${token()}` },
        body:    JSON.stringify({ assigned_devs:selectedDevs, notes, priority }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send to dev queue");
      onSent(data);
    } catch (err) { setError(err.message); }
    finally { setSending(false); }
  };

  return (
    <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16 }}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{ background:"var(--t-panel)",border:"1px solid rgba(148,163,184,0.18)",borderRadius:16,width:"100%",maxWidth:520,boxShadow:"0 32px 80px rgba(0,0,0,0.6)" }}>
        <div style={{ padding:"18px 24px",borderBottom:"1px solid var(--t-border-soft)",display:"flex",alignItems:"center",justifyContent:"space-between" }}>
          <div>
            <div style={{ fontWeight:700,fontSize:15,color:"var(--t-text-primary)",display:"flex",alignItems:"center",gap:8 }}>
              <Code size={16} style={{ color:"var(--t-accent)" }}/> Send to Dev Queue
            </div>
            <div style={{ fontSize:12,color:"var(--t-text-faint)",marginTop:2 }}>{ticket.ticket_number} — {ticket.subject}</div>
          </div>
          <button onClick={onClose} style={{ background:"none",border:"none",color:"var(--t-text-faint)",fontSize:20,cursor:"pointer" }}>×</button>
        </div>
        <div style={{ padding:24 }}>
          {error && <div style={{ background:"rgba(220,38,38,0.1)",border:"1px solid rgba(220,38,38,0.3)",borderRadius:8,padding:"10px 14px",color:"#f87171",fontSize:13,marginBottom:16 }}>⚠ {error}</div>}

          <div style={{ marginBottom:16 }}>
            <label style={{ fontSize:12,color:"var(--t-text-faint)",display:"block",marginBottom:8 }}>Assign to Developers *</label>
            <div style={{ display:"flex",flexDirection:"column",gap:6,maxHeight:200,overflowY:"auto" }}>
              {agents.map(a => (
                <div key={a.id}
                  onClick={() => toggleDev(a.id)}
                  style={{
                    display:"flex",alignItems:"center",gap:10,padding:"8px 12px",borderRadius:8,cursor:"pointer",
                    background: selectedDevs.includes(a.id) ? "var(--t-accent-softer)" : "var(--t-input)",
                    border: `1px solid ${selectedDevs.includes(a.id) ? "var(--t-accent)" : "var(--t-border-soft)"}`,
                  }}>
                  <div style={{ width:18,height:18,borderRadius:4,border:`2px solid ${selectedDevs.includes(a.id)?"var(--t-accent)":"var(--t-border-strong)"}`,background:selectedDevs.includes(a.id)?"var(--t-accent)":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                    {selectedDevs.includes(a.id) && <span style={{ color:"#fff",fontSize:11,fontWeight:700 }}>✓</span>}
                  </div>
                  <div>
                    <div style={{ fontSize:13,fontWeight:600,color:"var(--t-text-primary)" }}>{a.first_name} {a.last_name}</div>
                    <div style={{ fontSize:11,color:"var(--t-text-faint)" }}>{a.role} · {a.email}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:12,color:"var(--t-text-faint)",display:"block",marginBottom:5 }}>Priority</label>
            <select value={priority} onChange={e=>setPriority(e.target.value)}
              style={{ background:"var(--t-card-input)",border:"1px solid rgba(99,162,255,0.3)",borderRadius:7,padding:"8px 12px",color:"var(--t-text-primary)",fontSize:13,width:"100%",cursor:"pointer" }}>
              {["Critical","High","Medium","Low"].map(p=><option key={p}>{p}</option>)}
            </select>
          </div>

          <div style={{ marginBottom:4 }}>
            <label style={{ fontSize:12,color:"var(--t-text-faint)",display:"block",marginBottom:5 }}>Notes for Dev Team</label>
            <textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={3} placeholder="Add any context or instructions for the dev team..."
              style={{ background:"var(--t-card-input)",border:"1px solid rgba(99,162,255,0.3)",borderRadius:7,padding:"8px 12px",color:"var(--t-text-primary)",fontSize:13,width:"100%",resize:"vertical",boxSizing:"border-box" }}/>
          </div>

          <div style={{ fontSize:12,color:"var(--t-text-body)",padding:"8px 12px",background:"var(--t-accent-softer)",border:"1px solid var(--t-accent-soft)",borderRadius:7,marginTop:12 }}>
            ✨ Claude AI will generate a professional briefing email and send it to the selected developers.
          </div>
        </div>
        <div style={{ padding:"16px 24px",borderTop:"1px solid var(--t-border-soft)",display:"flex",gap:10,justifyContent:"flex-end" }}>
          <button onClick={onClose} style={{ background:"transparent",color:"var(--t-text-muted)",border:"1px solid rgba(148,163,184,0.18)",padding:"9px 18px",borderRadius:8,fontWeight:600,fontSize:13,cursor:"pointer" }}>Cancel</button>
          <button onClick={handleSend} disabled={sending||!selectedDevs.length}
            style={{ background:"var(--t-accent-grad)",color:"#fff",border:"none",padding:"9px 18px",borderRadius:8,fontWeight:600,fontSize:13,cursor:"pointer",opacity:(!selectedDevs.length||sending)?0.5:1,boxShadow:"var(--t-btn-shadow, var(--t-accent-glow))" }}>
            {sending ? "Sending…" : "🔧 Send to Dev Queue"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Schedule Ticket Modal ────────────────────────────────────
// Moves an active ticket to a scheduled (On Hold) state and sets one or more task
// reminders. The system fires each reminder (tech email + internal note + push) at
// its time; the ticket stays On Hold with a blue "Scheduled for…" badge.
function ScheduleModal({ ticket, onClose, onScheduled }) {
  const [scheduledAt, setScheduledAt] = useState("");
  const [reminders,   setReminders]   = useState([{ remind_at: "", note: "" }]);
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState("");

  const setRem = (i, key, val) => setReminders(p => p.map((r, idx) => idx === i ? { ...r, [key]: val } : r));
  const addRem = () => setReminders(p => [...p, { remind_at: "", note: "" }]);
  const delRem = (i) => setReminders(p => p.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    if (!scheduledAt) { setError("Pick a date & time to schedule this ticket for."); return; }
    if (new Date(scheduledAt).getTime() <= Date.now()) { setError("Scheduled time must be in the future."); return; }
    setSaving(true); setError("");
    try {
      const cleanReminders = reminders
        .filter(r => r.remind_at || r.note)
        .map(r => ({ remind_at: r.remind_at || scheduledAt, note: r.note }));
      const res = await scheduleTicket(ticket.id, {
        scheduled_at: new Date(scheduledAt).toISOString(),
        reminders: cleanReminders.map(r => ({ remind_at: new Date(r.remind_at).toISOString(), note: r.note })),
      });
      onScheduled(res.data);
    } catch (err) { setError(err.response?.data?.error || err.message || "Failed to schedule"); }
    finally { setSaving(false); }
  };

  const fld = { background:"var(--t-card-input)",border:"1px solid rgba(99,162,255,0.3)",borderRadius:7,padding:"8px 12px",color:"var(--t-text-primary)",fontSize:13,width:"100%",boxSizing:"border-box" };

  return (
    <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16 }}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{ background:"var(--t-panel)",border:"1px solid rgba(148,163,184,0.18)",borderRadius:16,width:"100%",maxWidth:560,maxHeight:"90vh",overflowY:"auto",boxShadow:"0 32px 80px rgba(0,0,0,0.6)" }}>
        <div style={{ padding:"18px 24px",borderBottom:"1px solid var(--t-border-soft)",display:"flex",alignItems:"center",justifyContent:"space-between" }}>
          <div>
            <div style={{ fontWeight:700,fontSize:15,color:"var(--t-text-primary)",display:"flex",alignItems:"center",gap:8 }}>
              <CalendarClock size={16} style={{ color:"#3b82f6" }}/> Schedule Ticket
            </div>
            <div style={{ fontSize:12,color:"var(--t-text-faint)",marginTop:2 }}>{ticket.ticket_number} — {ticket.subject}</div>
          </div>
          <button onClick={onClose} style={{ background:"none",border:"none",color:"var(--t-text-faint)",fontSize:20,cursor:"pointer" }}>×</button>
        </div>
        <div style={{ padding:24 }}>
          {error && <div style={{ background:"rgba(220,38,38,0.1)",border:"1px solid rgba(220,38,38,0.3)",borderRadius:8,padding:"10px 14px",color:"#f87171",fontSize:13,marginBottom:16 }}>⚠ {error}</div>}

          <div style={{ marginBottom:18 }}>
            <label style={{ fontSize:12,color:"var(--t-text-faint)",display:"block",marginBottom:5 }}>Scheduled for *</label>
            <input type="datetime-local" value={scheduledAt} onChange={e=>setScheduledAt(e.target.value)} style={fld}/>
            <div style={{ fontSize:11,color:"var(--t-text-faint)",marginTop:5 }}>The ticket goes On Hold with a “Scheduled for…” badge and its SLA pauses until you work it.</div>
          </div>

          <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8 }}>
            <label style={{ fontSize:12,color:"var(--t-text-faint)" }}>Task reminders</label>
            <button onClick={addRem} style={{ background:"transparent",color:"#3b82f6",border:"1px solid rgba(59,130,246,0.4)",padding:"4px 10px",borderRadius:7,fontSize:12,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:4 }}>
              <Plus size={13}/> Add reminder
            </button>
          </div>
          {reminders.map((r, i) => (
            <div key={i} style={{ border:"1px solid rgba(148,163,184,0.18)",borderRadius:8,padding:12,marginBottom:10,background:"var(--t-input)" }}>
              <div style={{ display:"flex",gap:8,alignItems:"center",marginBottom:8 }}>
                <input type="datetime-local" value={r.remind_at} onChange={e=>setRem(i,"remind_at",e.target.value)} style={{ ...fld, flex:1 }}/>
                {reminders.length > 1 && <button onClick={()=>delRem(i)} style={{ background:"none",border:"none",color:"var(--t-text-faint)",cursor:"pointer" }}><X size={16}/></button>}
              </div>
              <input value={r.note} onChange={e=>setRem(i,"note",e.target.value)} placeholder="What will you do? e.g. Apply firewall change, call client back…" style={fld}/>
            </div>
          ))}
          <div style={{ fontSize:11,color:"var(--t-text-faint)",marginTop:4 }}>Blank reminder time defaults to the scheduled time. Each reminder emails you, posts an internal note, and pushes a notification.</div>
        </div>
        <div style={{ padding:"16px 24px",borderTop:"1px solid var(--t-border-soft)",display:"flex",gap:10,justifyContent:"flex-end" }}>
          <button onClick={onClose} style={{ background:"transparent",color:"var(--t-text-muted)",border:"1px solid rgba(148,163,184,0.18)",padding:"9px 18px",borderRadius:8,fontWeight:600,fontSize:13,cursor:"pointer" }}>Cancel</button>
          <button onClick={handleSave} disabled={saving||!scheduledAt}
            style={{ background:"linear-gradient(135deg,#2563eb,#3b82f6)",color:"#fff",border:"none",padding:"9px 18px",borderRadius:8,fontWeight:600,fontSize:13,cursor:"pointer",opacity:(!scheduledAt||saving)?0.5:1,boxShadow:"0 4px 16px rgba(37,99,235,0.4)" }}>
            {saving ? "Scheduling…" : "📅 Schedule Ticket"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Relaunch FTR Modal ───────────────────────────────────────
// Hands a ticket back to the FTR bot mid-thread. The bot re-reads the whole ticket —
// conversation, every tech reply, and the internal work log — and continues from
// wherever the last tech or the last customer reply left off, rather than re-triaging.
// Confirmed rather than fire-on-click because a run ends in an email to the customer.
const FTR_OFF_REASONS = {
  tech_engaged:        "a technician replied on the thread (that always switches the bot off)",
  self_escalated:      "the bot escalated it to a human",
  ftr_run_failed:      "its last run failed and the ticket was handed to a technician",
  manual_only_sender:  "this sender is on the manual-only list",
};
function RelaunchFtrModal({ ticket, onClose, onLaunched }) {
  const [busy,  setBusy]  = useState(false);
  const [error, setError] = useState("");
  const offReason = ticket.ftr_disabled_at
    ? (FTR_OFF_REASONS[ticket.ftr_disabled_reason] || ticket.ftr_disabled_reason || "it was switched off")
    : null;

  const go = async () => {
    setBusy(true); setError("");
    try {
      // Force only when the bot is actually switched off — the reason is shown above,
      // so clicking Relaunch is the tech's explicit consent to turn it back on.
      const res = await relaunchFtr(ticket.id, !!ticket.ftr_disabled_at);
      onLaunched(res.data);
    } catch (err) {
      const d = err.response?.data;
      setError([d?.error, d?.reason].filter(Boolean).join(" — ") || err.message || "Failed to relaunch FTR");
    } finally { setBusy(false); }
  };

  return (
    <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16 }}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{ background:"var(--t-panel)",border:"1px solid rgba(148,163,184,0.18)",borderRadius:16,width:"100%",maxWidth:520,maxHeight:"90vh",overflowY:"auto",boxShadow:"0 32px 80px rgba(0,0,0,0.6)" }}>
        <div style={{ padding:"18px 24px",borderBottom:"1px solid var(--t-border-soft)",display:"flex",alignItems:"center",justifyContent:"space-between" }}>
          <div>
            <div style={{ fontWeight:700,fontSize:15,color:"var(--t-text-primary)",display:"flex",alignItems:"center",gap:8 }}>
              <Bot size={16} style={{ color:"#22d3ee" }}/> Relaunch FTR on this ticket
            </div>
            <div style={{ fontSize:12,color:"var(--t-text-faint)",marginTop:2 }}>{ticket.ticket_number} — {ticket.subject}</div>
          </div>
          <button onClick={onClose} style={{ background:"none",border:"none",color:"var(--t-text-faint)",fontSize:20,cursor:"pointer" }}>×</button>
        </div>
        <div style={{ padding:24 }}>
          {error && <div style={{ background:"rgba(220,38,38,0.1)",border:"1px solid rgba(220,38,38,0.3)",borderRadius:8,padding:"10px 14px",color:"#f87171",fontSize:13,marginBottom:16 }}>⚠ {error}</div>}

          <div style={{ fontSize:13,color:"var(--t-text-muted)",lineHeight:1.65 }}>
            The bot will read the whole ticket — the conversation, every tech reply, and the internal
            work log of what has already been done — then <strong style={{ color:"var(--t-text-primary)" }}>pick up
            where the last tech or the last customer reply left off</strong>. It finishes what is still
            outstanding with its own tools, and escalates back to a human for anything it can't.
          </div>

          <div style={{ marginTop:14,background:"rgba(34,211,238,0.07)",border:"1px solid rgba(34,211,238,0.22)",borderRadius:8,padding:"11px 14px",fontSize:12.5,color:"#67e8f9",lineHeight:1.6 }}>
            It will <strong>email the customer</strong> when it's done, and the ticket will be assigned to
            the bot while it works.
          </div>

          {offReason && (
            <div style={{ marginTop:12,background:"rgba(251,191,36,0.08)",border:"1px solid rgba(251,191,36,0.28)",borderRadius:8,padding:"11px 14px",fontSize:12.5,color:"#fbbf24",lineHeight:1.6 }}>
              FTR is currently <strong>off</strong> on this ticket because {offReason}. Relaunching turns it
              back on — it will reply to the customer again.
            </div>
          )}
        </div>
        <div style={{ padding:"16px 24px",borderTop:"1px solid var(--t-border-soft)",display:"flex",gap:10,justifyContent:"flex-end" }}>
          <button onClick={onClose} style={{ background:"transparent",color:"var(--t-text-muted)",border:"1px solid rgba(148,163,184,0.18)",padding:"9px 18px",borderRadius:8,fontWeight:600,fontSize:13,cursor:"pointer" }}>Cancel</button>
          <button onClick={go} disabled={busy}
            style={{ background:"linear-gradient(135deg,#0e7490,#22d3ee)",color:"#04252b",border:"none",padding:"9px 18px",borderRadius:8,fontWeight:700,fontSize:13,cursor:busy?"wait":"pointer",opacity:busy?0.6:1,boxShadow:"0 4px 16px rgba(34,211,238,0.35)" }}>
            {busy ? "Launching…" : "🤖 Relaunch FTR"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Send to Infrastructure Modal ────────────────────────────
function SendToInfraModal({ ticket, agents, onClose, onSent }) {
  const [form, setForm] = useState({
    title:       ticket.subject,
    description: ticket.description || "",
    priority:    ticket.priority,
    due_date:    "",
    notes:       "",
  });
  const [assignedTo, setAssignedTo] = useState([]);
  const [sending,    setSending]    = useState(false);
  const [error,      setError]      = useState("");

  const toggleMember = (id) => setAssignedTo(p => p.includes(id) ? p.filter(m=>m!==id) : [...p, id]);
  const set = (k, v) => setForm(p => ({ ...p, [k]:v }));

  const handleSend = async () => {
    if (!form.title.trim()) { setError("Project title is required."); return; }
    if (!assignedTo.length) { setError("Assign at least one team member."); return; }
    setSending(true); setError("");
    try {
      const res  = await fetch(`${API}/tickets/${ticket.id}/send-to-infrastructure`, {
        method:  "POST",
        headers: { "Content-Type":"application/json", Authorization:`Bearer ${token()}` },
        body:    JSON.stringify({ ...form, assigned_to:assignedTo }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to create infrastructure project");
      onSent(data);
    } catch (err) { setError(err.message); }
    finally { setSending(false); }
  };

  const fieldStyle = { background:"var(--t-card-input)",border:"1px solid rgba(99,162,255,0.3)",borderRadius:7,padding:"8px 12px",color:"var(--t-text-primary)",fontSize:13,width:"100%",boxSizing:"border-box" };
  const labelStyle = { fontSize:12,color:"var(--t-text-faint)",display:"block",marginBottom:5 };

  return (
    <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16 }}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{ background:"var(--t-panel)",border:"1px solid rgba(148,163,184,0.18)",borderRadius:16,width:"100%",maxWidth:560,maxHeight:"90vh",display:"flex",flexDirection:"column",boxShadow:"0 32px 80px rgba(0,0,0,0.6)" }}>
        <div style={{ padding:"18px 24px",borderBottom:"1px solid var(--t-border-soft)",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0 }}>
          <div>
            <div style={{ fontWeight:700,fontSize:15,color:"var(--t-text-primary)",display:"flex",alignItems:"center",gap:8 }}>
              <Wrench size={16} style={{ color:"#06b6d4" }}/> Create Project
            </div>
            <div style={{ fontSize:12,color:"var(--t-text-faint)",marginTop:2 }}>{ticket.ticket_number} — {ticket.subject}</div>
          </div>
          <button onClick={onClose} style={{ background:"none",border:"none",color:"var(--t-text-faint)",fontSize:20,cursor:"pointer" }}>×</button>
        </div>
        <div style={{ padding:24,overflowY:"auto",flex:1 }}>
          {error && <div style={{ background:"rgba(220,38,38,0.1)",border:"1px solid rgba(220,38,38,0.3)",borderRadius:8,padding:"10px 14px",color:"#f87171",fontSize:13,marginBottom:16 }}>⚠ {error}</div>}

          <div style={{ marginBottom:12 }}>
            <label style={labelStyle}>Project Title *</label>
            <input value={form.title} onChange={e=>set("title",e.target.value)} style={fieldStyle} placeholder="e.g. Network Upgrade — Main Office"/>
          </div>
          <div style={{ marginBottom:12 }}>
            <label style={labelStyle}>Description</label>
            <textarea value={form.description} onChange={e=>set("description",e.target.value)} rows={3}
              style={{ ...fieldStyle,resize:"vertical" }} placeholder="Describe the project scope and objectives..."/>
          </div>
          <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12 }}>
            <div>
              <label style={labelStyle}>Priority</label>
              <select value={form.priority} onChange={e=>set("priority",e.target.value)} style={{ ...fieldStyle,cursor:"pointer" }}>
                {["Critical","High","Medium","Low"].map(p=><option key={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Due Date</label>
              <input type="date" value={form.due_date} onChange={e=>set("due_date",e.target.value)} style={fieldStyle}/>
            </div>
          </div>

          <div style={{ marginBottom:12 }}>
            <label style={labelStyle}>Assign Team Members *</label>
            <div style={{ display:"flex",flexDirection:"column",gap:6,maxHeight:180,overflowY:"auto" }}>
              {agents.map(a => (
                <div key={a.id}
                  onClick={() => toggleMember(a.id)}
                  style={{
                    display:"flex",alignItems:"center",gap:10,padding:"8px 12px",borderRadius:8,cursor:"pointer",
                    background: assignedTo.includes(a.id) ? "rgba(6,182,212,0.1)" : "var(--t-input)",
                    border: `1px solid ${assignedTo.includes(a.id) ? "rgba(6,182,212,0.4)" : "rgba(148,163,184,0.18)"}`,
                  }}>
                  <div style={{ width:18,height:18,borderRadius:4,border:`2px solid ${assignedTo.includes(a.id)?"#06b6d4":"#56677e"}`,background:assignedTo.includes(a.id)?"#06b6d4":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                    {assignedTo.includes(a.id) && <span style={{ color:"#fff",fontSize:11,fontWeight:700 }}>✓</span>}
                  </div>
                  <div>
                    <div style={{ fontSize:13,fontWeight:600,color:"var(--t-text-primary)" }}>{a.first_name} {a.last_name}</div>
                    <div style={{ fontSize:11,color:"var(--t-text-faint)" }}>{a.role} · {a.email}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginBottom:4 }}>
            <label style={labelStyle}>Additional Notes</label>
            <textarea value={form.notes} onChange={e=>set("notes",e.target.value)} rows={2} placeholder="Any additional context..."
              style={{ ...fieldStyle,resize:"vertical" }}/>
          </div>

          <div style={{ fontSize:12,color:"#06b6d4",padding:"8px 12px",background:"rgba(6,182,212,0.08)",border:"1px solid rgba(6,182,212,0.2)",borderRadius:7,marginTop:12 }}>
            ✨ Claude AI will generate a professional project briefing email and send it to the assigned team.
          </div>
        </div>
        <div style={{ padding:"16px 24px",borderTop:"1px solid var(--t-border-soft)",display:"flex",gap:10,justifyContent:"flex-end",flexShrink:0 }}>
          <button onClick={onClose} style={{ background:"transparent",color:"var(--t-text-muted)",border:"1px solid rgba(148,163,184,0.18)",padding:"9px 18px",borderRadius:8,fontWeight:600,fontSize:13,cursor:"pointer" }}>Cancel</button>
          <button onClick={handleSend} disabled={sending||!form.title.trim()||!assignedTo.length}
            style={{ background:"linear-gradient(135deg,#0891b2,#06b6d4)",color:"#fff",border:"none",padding:"9px 18px",borderRadius:8,fontWeight:600,fontSize:13,cursor:"pointer",opacity:(!form.title.trim()||!assignedTo.length||sending)?0.5:1 }}>
            {sending ? "Creating…" : "🏗️ Create Project"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Success toast ────────────────────────────────────────────
function Toast({ message, color, onDismiss }) {
  useEffect(() => { const t = setTimeout(onDismiss, 4000); return () => clearTimeout(t); }, [onDismiss]);
  return (
    <div style={{ position:"fixed",bottom:24,right:24,background:color,color:"#fff",padding:"12px 20px",borderRadius:10,fontSize:13,fontWeight:600,boxShadow:"0 8px 32px rgba(0,0,0,0.4)",zIndex:9999,display:"flex",alignItems:"center",gap:10 }}>
      {color === "#dc2626" ? "⚠" : "✓"} {message}
      <button onClick={onDismiss} style={{ background:"none",border:"none",color:"#fff",cursor:"pointer",fontSize:16,lineHeight:1 }}>×</button>
    </div>
  );
}

// ─── Small pieces ─────────────────────────────────────────────
const AI_EMAILS = /^(ai|adserver-ai)@midtc\.com$/i;
const isEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(e || "").trim());
const lc = (e) => String(e || "").trim().toLowerCase();
const firstName = (n) => String(n || "").trim().split(/\s+/)[0] || "";

// Where a CC on this ticket came from, from tickets.cc_log (kept since 2026-10-23).
function ccOrigin(ticket, email) {
  const log = (ticket.cc_log || []).filter(x => lc(x.email) === lc(email) && !x.removed);
  const last = log[log.length - 1];
  const when = last?.at ? ` · ${new Date(last.at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : "";
  if (!last) {
    return (ticket.original_cc_emails || []).some(x => lc(x) === lc(email)) ? "On the original request" : "CC'd before CC history was kept";
  }
  switch (last.via) {
    case "original":    return `On the original request${when}`;
    case "email_reply": return `Copied in on ${last.by ? `${last.by}'s` : "an"} email reply${when}`;
    case "staff":       return `Added by ${last.by || "staff"}${when}`;
    case "portal":      return `Added in the portal${last.by ? ` by ${last.by}` : ""}${when}`;
    default:            return `Added${when}`;
  }
}
function ccHistoryLine(x) {
  const when = x.at ? new Date(x.at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";
  if (x.removed) return `${x.email} removed by ${x.by || "staff"} · ${when}`;
  const how = { original: "on the original request", email_reply: `copied in on ${x.by || "an"} email reply`, staff: `added by ${x.by || "staff"}`, portal: `added in the portal${x.by ? ` by ${x.by}` : ""}` }[x.via] || "added";
  return `${x.email} ${how} · ${when}`;
}

function slaInfo(t) {
  if (["Resolved", "Closed"].includes(t.status)) return { tone: "green", text: t.status };
  if (t.sla_status === "breach") return { tone: "red", text: "SLA breached" };
  if (t.sla_status === "hold" || t.sla_hold) return { tone: "orange", text: "SLA on hold" };
  if (!t.sla_due_at) return { tone: "gray", text: "No SLA deadline" };
  const ms = new Date(t.sla_due_at).getTime() - Date.now();
  if (ms <= 0) return { tone: "red", text: "SLA due now" };
  const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000);
  return { tone: h < 2 ? "amber" : "green", text: `In SLA · ${h ? `${h}h ` : ""}${m}m left` };
}

// Inline markdown the composer offers (Bold, Code) → HTML, for bodies that are sent as
// HTML (anything carrying the signature). Plain-text bodies keep the markdown, which
// the in-app renderer already understands.
const mdInline = (s) => s.replace(/\*\*([^*\n]+)\*\*/g, "<b>$1</b>").replace(/`([^`\n]+)`/g, "<code>$1</code>");

function Popover({ open, onClose, children, align = "right", width = 260, keepMounted = false }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target) && !e.target.closest?.("[data-popover-trigger]")) onClose(); };
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open, onClose]);
  if (!open && !keepMounted) return null;
  return (
    <div ref={ref} className="cv-pop" role="menu" style={{ top: "calc(100% + 6px)", [align]: 0, width, display: open ? "block" : "none" }}>
      {children}
    </div>
  );
}

// ─── Main TicketDetail ────────────────────────────────────────
// Previous/next swap the :id under the same route. Keying by id gives every ticket a
// fresh page, so nothing (an unsent draft, open messages, filters) carries over.
export default function TicketDetail() {
  const { id } = useParams();
  return <TicketDetailPage key={id} />;
}

function TicketDetailPage() {
  const { id }     = useParams();
  const navigate   = useNavigate();
  const location   = useLocation();
  const { agent }  = useAuth();
  // Inside the desktop app (/embed) the list and dev tickets stay in the embed; other
  // pages (a contact, a client, a project) open in their own window.
  const embed      = location.pathname.startsWith("/embed");
  const listPath   = embed ? "/embed/tickets" : "/tickets";
  const go = (to) => (embed && !to.startsWith("/embed") && !to.startsWith("/tickets") && !to.startsWith("/dev-tickets")
    ? window.open(`https://ticketing.midtc.com${to}`, "_blank", "noopener")
    : navigate(embed && to.startsWith("/dev-tickets") ? `/embed${to}` : embed && to.startsWith("/tickets") ? `/embed${to}` : to));
  const isMobile   = useIsMobile();
  // Who else has this ticket open right now (2026-11-03) — "Watching" beside "Assigned", so two
  // techs don't collide. Beats every 15 s while this tab is visible; says so at once when you leave.
  const [watchers,   setWatchers]   = useState([]);
  useEffect(() => {
    let live = true;
    const beat = (leaving = false) => fetch(`${API}/tickets/${id}/presence`, {
      method: "POST", keepalive: leaving,
      headers: { Authorization: `Bearer ${token()}`, "Content-Type": "application/json" },
      body: JSON.stringify(leaving ? { leaving: true } : {}),
    }).then(r => (r.ok ? r.json() : null)).then(d => { if (live && d && !leaving) setWatchers(d.viewers || []); }).catch(() => {});
    const tick = () => { if (!document.hidden) beat(); };
    tick();
    const timer = setInterval(tick, 15000);
    const onHide = () => beat(true);
    document.addEventListener("visibilitychange", tick);
    window.addEventListener("pagehide", onHide);
    return () => { live = false; clearInterval(timer); document.removeEventListener("visibilitychange", tick); window.removeEventListener("pagehide", onHide); beat(true); };
  }, [id]);
  const [ticket,     setTicket]     = useState(null);
  const [agents,     setAgents]     = useState([]);
  const [comments,   setComments]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  // Only a 404 from the server means "not found". A timeout / dropped connection / 5xx
  // used to fall through to "Ticket not found" too (TKT-26528 on a slow link, 11-04).
  const [loadError,  setLoadError]  = useState(null);   // null | "notfound" | "failed"
  const [reloadKey,  setReloadKey]  = useState(0);
  const [updating,   setUpdating]   = useState(false);
  const [toast,      setToast]      = useState(null);
  const [linkedDev,  setLinkedDev]  = useState(null);

  // Modals and menus
  const [showDev,         setShowDev]         = useState(false);
  const [showInfra,       setShowInfra]       = useState(false);
  const [showSecureLink,  setShowSecureLink]  = useState(false);
  const [showSchedule,    setShowSchedule]    = useState(false);
  const [showRelaunchFtr, setShowRelaunchFtr] = useState(false);
  const [remoteOpen,      setRemoteOpen]      = useState(false);
  const [moreOpen,        setMoreOpen]        = useState(false);

  // Timeline
  const [filter,   setFilter]   = useState("all");
  const [search,   setSearch]   = useState("");
  const [expanded, setExpanded] = useState(() => new Set());
  const [focusId,  setFocusId]  = useState(null);
  const scrollRef  = useRef(null);
  const nodeRefs   = useRef(new Map());
  const seenIds    = useRef(null);       // entry ids already shown — new arrivals open
  const firstPaint = useRef(true);

  // Composer
  const [composeOpen, setComposeOpen] = useState(false);
  const [mode,        setMode]        = useState("reply");   // reply | note
  const [reply,       setReply]       = useState(() => { try { return sessionStorage.getItem(`midtc_draft_${id}`) || ""; } catch { return ""; } });
  const [attachments, setAttachments] = useState([]);
  const [ccAdd,       setCcAdd]       = useState([]);
  const [ccDraft,     setCcDraft]     = useState("");
  const [ccEditing,   setCcEditing]   = useState(false);
  const [sending,     setSending]     = useState(false);
  const [sendError,   setSendError]   = useState("");
  const [uploadPct,   setUploadPct]   = useState(null);
  const [aiSuggest,   setAiSuggest]   = useState({ visible:false, loading:false, text:"" });
  const [showSig,     setShowSig]     = useState(false);
  const fileInputRef = useRef(null);
  const textRef      = useRef(null);

  // Details panel
  const [ccInput,     setCcInput]     = useState("");
  const [ccError,     setCcError]     = useState("");
  const [ccSaving,    setCcSaving]    = useState(false);
  const [ccHistory,   setCcHistory]   = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState(false);

  const myRoles = useMemo(() => [agent?.role, ...(agent?.roles || [])].filter(Boolean), [agent]);
  const canSendToQueue = myRoles.some(r => ["admin", "manager", "technician"].includes(r));
  const canSeeDevQueue = myRoles.some(r => ["admin", "dev"].includes(r));
  const isNote = mode === "note";

  // The thread pane owns the scrolling, not Layout's <main>.
  useEffect(() => {
    const main = document.querySelector("main");
    if (!main) return;
    const prev = main.style.overflow;
    main.style.overflow = "hidden";
    return () => { main.style.overflow = prev; };
  }, []);

  // Keep an unsent reply if the page is left or reloaded (per ticket, this tab only).
  useEffect(() => {
    try { if (reply) sessionStorage.setItem(`midtc_draft_${id}`, reply); else sessionStorage.removeItem(`midtc_draft_${id}`); } catch { /* ignore */ }
  }, [reply, id]);

  const loadAll = useCallback(async () => {
    const [t, c] = await Promise.all([getTicket(id), getTicketComments(id)]);
    setTicket(t.data);
    setComments(c.data || []);
    return t.data;
  }, [id]);

  useEffect(() => {
    let alive = true;
    setLoading(true); setLoadError(null); setTicket(null); seenIds.current = null; firstPaint.current = true; setLinkedDev(null);
    setReply(() => { try { return sessionStorage.getItem(`midtc_draft_${id}`) || ""; } catch { return ""; } });
    setAttachments([]); setCcAdd([]); setComposeOpen(false); setSendError(""); setFilter("all"); setSearch("");
    Promise.all([loadAll(), getAgents().then(a => { if (alive) setAgents(a.data || []); })])
      .catch((err) => { console.error(err); if (alive) setLoadError(err?.response?.status === 404 ? "notfound" : "failed"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id, loadAll, reloadKey]);

  // The dev ticket this ticket was sent to, if any (Dev Queue is admin/dev only).
  useEffect(() => {
    if (!canSeeDevQueue || !id) return;
    fetch(`${API}/dev-tickets`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => (r.ok ? r.json() : []))
      .then(list => setLinkedDev((Array.isArray(list) ? list : []).find(d => d.source_ticket_id === id) || null))
      .catch(() => {});
  }, [id, canSeeDevQueue, ticket?.status]);

  // Refresh every 60 s, but never while something is being saved or sent.
  useEffect(() => {
    const t = setInterval(() => { if (!sending && !updating) loadAll().catch(() => {}); }, 60000);
    return () => clearInterval(t);
  }, [loadAll, sending, updating]);

  // ── Timeline entries ─────────────────────────────────────────
  const aiIds = useMemo(() => new Set(agents.filter(a => (a.roles || []).includes("ai") || AI_EMAILS.test(a.email || "")).map(a => a.id)), [agents]);
  const entries = useMemo(() => {
    if (!ticket) return [];
    const requester = ticket.submitter_name || ticket.client_name || "Client";
    const requesterEmail = lc(ticket.submitter_email);
    const ccNow = new Set((ticket.cc_emails || []).map(lc));
    const ccText = (list) => (list && list.length ? ` · Cc ${list.join(", ")}` : "");
    const out = [{
      id: "orig", kind: "client", tagKind: "client", role: "Original request", name: requester,
      to: [[ticket.submitter_title, ticket.client_name].filter(Boolean).join(", "), ticket.original_cc_emails?.length ? `Cc ${ticket.original_cc_emails.join(", ")}` : ""].filter(Boolean).join(" · "),
      at: ticket.created_at, body: ticket.description || "", files: ticket.attachments || [], badges: ticket.source === "email" ? ["by email"] : ticket.source === "portal" ? ["portal"] : [],
    }];
    for (const r of ticket.responses || []) {
      const isAi = !!r.agent_id && (aiIds.has(r.agent_id) || AI_EMAILS.test(r.agent_email || ""));
      const kind = r.is_internal ? "note" : r.agent_id ? (isAi ? "ai" : "agent") : "client";
      const name = r.agent_name || r.contact_name || r.sender_name || r.sender_email || "Client";
      const badges = [];
      let role = kind === "note" ? "Internal note" : kind === "ai" ? "AI reply" : kind === "agent" ? "Technician" : "Client";
      if (kind === "client") {
        const fromRequester = r.contact_id ? r.contact_id === ticket.contact_id : (r.sender_email ? lc(r.sender_email) === requesterEmail : true);
        if (!fromRequester) badges.push(ccNow.has(lc(r.sender_email)) || r.contact_id ? "CC'd" : "Not on the ticket");
        if (r.source === "email") badges.push("by email");
      }
      if (kind === "agent" && r.is_ai_generated) badges.push("AI-drafted");
      if (r.source === "import") badges.push("Imported");
      const to = kind === "note" ? "Only MIDTC staff can see this"
        : kind === "client" ? `To MIDTC Support${ccText(r.cc_emails)}`
        : `To ${requester}${ccText(r.cc_emails)}`;
      out.push({ id: `r-${r.id}`, kind, tagKind: kind === "agent" ? "tech" : kind, role, name, to, at: r.created_at, body: r.body, files: r.attachments || [], badges, raw: r });
    }
    for (const c of comments) {
      const system = c.author_type === "system" || !c.author_name || c.author_name === "System";
      const text = stripHtml(c.body).replace(/\s+/g, " ").trim();
      if (system && text.length <= 170 && !/\n/.test(String(c.body || "").trim())) {
        out.push({ id: `c-${c.id}`, kind: "event", text, at: c.created_at });
      } else {
        out.push({
          id: `c-${c.id}`, kind: system ? "activity" : "note", tagKind: system ? "activity" : "note",
          role: system ? "Activity" : c.author_type === "imported" ? "Imported note" : c.is_internal ? "Work log" : "Comment",
          name: system ? (c.author_name || "System") : c.author_name, to: c.is_internal ? "Only MIDTC staff can see this" : "",
          at: c.created_at, body: c.body, files: [], badges: [],
        });
      }
    }
    for (const e of out) { e.preview = e.kind === "event" ? e.text : previewText(splitBody(e.body).main || e.body); e.search = `${e.name || ""} ${e.preview} ${stripHtml(e.body || e.text || "")}`.toLowerCase(); }
    const orig = out.shift();
    out.sort((a, b) => new Date(a.at) - new Date(b.at));
    return [orig, ...out];
  }, [ticket, comments, aiIds]);

  const KIND_FILTER = { all: () => true, client: k => k === "client", replies: k => k === "agent" || k === "ai", internal: k => k === "note", activity: k => k === "event" || k === "activity" };
  const counts = useMemo(() => {
    const c = { all: entries.length, client: 0, replies: 0, internal: 0, activity: 0 };
    for (const e of entries) for (const k of ["client", "replies", "internal", "activity"]) if (KIND_FILTER[k](e.kind)) c[k]++;
    return c;
  }, [entries]); // eslint-disable-line react-hooks/exhaustive-deps
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return entries.filter(e => KIND_FILTER[filter](e.kind) && (!q || e.search.includes(q)));
  }, [entries, filter, search]); // eslint-disable-line react-hooks/exhaustive-deps
  const messageIds = useMemo(() => visible.filter(e => e.kind !== "event").map(e => e.id), [visible]);

  // Open the original request, the newest message and the client's newest message on
  // first load; afterwards anything that arrives while the page is open opens itself.
  useEffect(() => {
    if (!entries.length) return;
    const msgs = entries.filter(e => e.kind !== "event");
    if (!seenIds.current) {
      const open = new Set(["orig"]);
      const last = msgs[msgs.length - 1]; if (last) open.add(last.id);
      const lastClient = [...msgs].reverse().find(e => e.kind === "client"); if (lastClient) open.add(lastClient.id);
      setExpanded(open);
      seenIds.current = new Set(entries.map(e => e.id));
      return;
    }
    const fresh = msgs.filter(e => !seenIds.current.has(e.id));
    entries.forEach(e => seenIds.current.add(e.id));
    if (fresh.length) setExpanded(prev => { const n = new Set(prev); fresh.forEach(e => n.add(e.id)); return n; });
  }, [entries]);

  // Land on the newest message; stay pinned to the bottom when new ones arrive if you were already there.
  const nearBottom = useRef(true);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => { nearBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 160; };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [loading]);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !entries.length) return;
    if (firstPaint.current || nearBottom.current) {
      requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
      firstPaint.current = false;
    }
  }, [entries.length, loading]);

  const toggle = useCallback((eid) => setExpanded(prev => { const n = new Set(prev); n.has(eid) ? n.delete(eid) : n.add(eid); return n; }), []);
  const allOpen = messageIds.length > 0 && messageIds.every(x => expanded.has(x));
  const expandAll = () => setExpanded(new Set(entries.filter(e => e.kind !== "event").map(e => e.id)));
  const collapseAll = () => setExpanded(new Set());
  const refFor = useCallback((eid) => (node) => { if (node) nodeRefs.current.set(eid, node); else nodeRefs.current.delete(eid); }, []);

  const openComposer = useCallback((m = "reply") => {
    setMode(m); setComposeOpen(true);
    setTimeout(() => textRef.current?.focus(), 30);
  }, []);
  // Stable, so the memoised conversation is not redrawn by typing in the reply box.
  const replyFrom = useCallback((e) => openComposer(e.kind === "note" ? "note" : "reply"), [openComposer]);

  // Keyboard: J/K next/previous message, Enter opens it, R reply, N note, E expand/collapse all.
  useEffect(() => {
    const onKey = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey || isTypingTarget(e.target)) return;
      if (showDev || showInfra || showSecureLink || showSchedule || showRelaunchFtr) return;
      const k = e.key.toLowerCase();
      if (k === "j" || k === "k") {
        if (!messageIds.length) return;
        e.preventDefault();
        const i = messageIds.indexOf(focusId);
        const next = k === "j" ? Math.min(messageIds.length - 1, i < 0 ? 0 : i + 1) : Math.max(0, i < 0 ? messageIds.length - 1 : i - 1);
        const nid = messageIds[next];
        setFocusId(nid);
        nodeRefs.current.get(nid)?.scrollIntoView({ block: "nearest", behavior: "smooth" });
      } else if (k === "enter" && focusId) { e.preventDefault(); toggle(focusId); }
      else if (k === "r") { e.preventDefault(); openComposer("reply"); }
      else if (k === "n") { e.preventDefault(); openComposer("note"); }
      else if (k === "e") { e.preventDefault(); allOpen ? collapseAll() : expandAll(); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }); // re-bound each render on purpose: it reads the current list and focus

  // ── Actions ─────────────────────────────────────────────────
  const handleUpdate = async (field, value) => {
    setUpdating(true);
    try {
      const res = await updateTicket(id, { [field]: value });
      setTicket(prev => ({ ...prev, ...res.data }));
      if (field === "assigned_to") setToast({ message: value ? "Ticket assigned" : "Ticket set to Unassigned", color: "#059669" });
      if (field === "cc_emails") getTicketComments(id).then(c => setComments(c.data || [])).catch(() => {});
      return true;
    } catch (e) {
      console.error(e);
      setToast({ message: e.response?.data?.error || `Could not update ${field.replace(/_/g, " ")}`, color: "#dc2626" });
      return false;
    } finally { setUpdating(false); }
  };

  const ccList = ticket?.cc_emails || [];
  const addCc = async (picked) => {
    const email = lc(typeof picked === "string" ? picked : ccInput);
    if (!email) return;
    if (!isEmail(email)) { setCcError("That doesn't look like an email address."); return; }
    if (ccList.map(lc).includes(email)) { setCcError("Already copied on this ticket."); return; }
    if (email === lc(ticket.submitter_email)) { setCcError("That's the person who opened the ticket — they always get replies."); return; }
    setCcSaving(true); setCcError("");
    if (await handleUpdate("cc_emails", [...ccList, email])) setCcInput("");
    setCcSaving(false);
  };
  const removeCc = async (email) => {
    setCcSaving(true);
    await handleUpdate("cc_emails", ccList.filter(e => lc(e) !== lc(email)));
    setCcSaving(false);
  };

  const handleSuggest = async () => {
    openComposer("reply");
    setAiSuggest({ visible:true, loading:true, text:"" });
    try {
      const res = await suggestResponse(id, "");
      const suggestion = res?.data?.suggestion;
      if (!suggestion) throw new Error("Empty response from server");
      setAiSuggest({ visible:true, loading:false, text:suggestion });
      setReply(suggestion);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data?.error || err?.message || "Unknown error";
      setAiSuggest({ visible:true, loading:false, text:`Couldn't write a suggestion: ${detail}` });
    }
  };

  const handleGenerateSummary = async () => {
    setGeneratingSummary(true);
    try {
      const res = await fetch(`${API}/tickets/${id}/generate-summary`, { method: "POST", headers: { Authorization: `Bearer ${token()}` } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed");
      setTicket(prev => ({ ...prev, ai_summary: data.summary }));
    } catch (e) { setToast({ message: "Couldn't generate the summary — try again in a moment.", color: "#dc2626" }); }
    finally { setGeneratingSummary(false); }
  };

  const agentSignature = (agent?.signature || "").replace(
    /(<img[^>]*?)style="[^"]*max-height:[^"]*"([^>]*>)/gi,
    '$1style="max-width:320px;width:100%;height:auto;display:block;margin-top:6px;"$2'
  );
  // One object per signature, not per render: React 19 rewrites the preview (and re-fetches its
  // images) whenever the {__html} object is new — i.e. on every keystroke. See HtmlContent.js.
  const sigHtml = useMemo(() => ({ __html: agentSignature }), [agentSignature]);

  const addFilesToReply = (incoming) => {
    const r = addWithinLimits(attachments, incoming);
    setAttachments(r.files);
    setSendError(r.notes.join(" "));
  };
  const handleReplyPaste = (e) => {
    const pasted = filesFromPaste(e);
    if (!pasted.length) return;
    e.preventDefault();
    addFilesToReply(pasted);
  };
  const wrapSelection = (left, right = left) => {
    const ta = textRef.current; if (!ta) return;
    const { selectionStart: a, selectionEnd: b, value } = ta;
    const inner = value.slice(a, b) || "text";
    const next = value.slice(0, a) + left + inner + right + value.slice(b);
    setReply(next);
    setTimeout(() => { ta.focus(); ta.setSelectionRange(a + left.length, a + left.length + inner.length); }, 0);
  };
  const addPendingCc = (picked) => {
    const email = lc(typeof picked === "string" ? picked : ccDraft);
    if (!email) { setCcEditing(false); return; }
    if (!isEmail(email)) { setSendError("That Cc address doesn't look right."); return; }
    if ([...ccList.map(lc), ...ccAdd].includes(email) || email === lc(ticket.submitter_email)) { setCcDraft(""); return; }
    setCcAdd(p => [...p, email]); setCcDraft(""); setSendError("");
  };

  const handleSend = async () => {
    if (sending || (!reply.trim() && !attachments.length)) return;
    // A Cc address typed but not confirmed with Enter still goes with this reply.
    const typed = lc(ccDraft);
    if (!isNote && typed && !isEmail(typed)) { setSendError("The Cc address doesn't look right — fix it or clear it, then send."); return; }
    const ccExtra = !isNote && typed && ![...ccList.map(lc), ...ccAdd].includes(typed) && typed !== lc(ticket.submitter_email) ? [...ccAdd, typed] : ccAdd;
    setSending(true); setSendError(""); setUploadPct(attachments.length ? 0 : null);
    try {
      const nl2br = (s) => s.replace(/\r\n/g, "\n").replace(/\r/g, "\n").replace(/\n/g, "<br>");
      const text = reply.trim() ? reply : `📎 Attached ${attachments.length} file${attachments.length > 1 ? "s" : ""}`;
      const fullBody = (!isNote && agentSignature)
        ? `${mdInline(nl2br(text))}<br><br><hr style="border:none;border-top:1px solid #e2e8f0;margin:16px 0"><div style="font-size:14px;color:#374151;">${agentSignature}</div>`
        : text;
      const fd = new FormData();
      fd.append("body",            fullBody);
      fd.append("is_internal",     String(isNote));
      fd.append("is_ai_generated", String(aiSuggest.visible && aiSuggest.text === reply));
      fd.append("ai_edited",       String(aiSuggest.visible && aiSuggest.text !== reply));
      if (!isNote && ccExtra.length) fd.append("cc_add", ccExtra.join(","));
      attachments.forEach(f => fd.append("attachments", f));
      await addResponse(id, fd, attachments.length ? { onUploadProgress: (p) => { if (p.total) setUploadPct(Math.round((p.loaded / p.total) * 100)); } } : undefined);
      const who = firstName(ticket.submitter_name) || "the client";
      const cc = [...ccList, ...ccExtra];
      setToast({ message: isNote ? "Internal note added" : ticket.contact_id ? `Reply sent to ${who}${cc.length ? ` (Cc ${cc.length})` : ""}` : "Reply saved — this ticket has no client contact to email", color: "#059669" });
      setReply(""); setAttachments([]); setCcAdd([]); setCcDraft(""); setCcEditing(false);
      setAiSuggest({ visible:false, loading:false, text:"" });
      setComposeOpen(false);
      nearBottom.current = true;
      await loadAll();
    } catch (e) {
      console.error(e);
      const st = e.response?.status;
      setSendError(st === 413 ? "Those files are too large to send together — send fewer at a time (up to 20 MB each)."
        : e.response?.data?.error || (e.message === "Network Error" ? "The connection dropped while sending — check your internet and try again. Your message is still here." : "The reply could not be sent — please try again. Your message is still here."));
    } finally { setSending(false); setUploadPct(null); }
  };

  // ── Previous / next through the list you came from ──────────
  const nav = useMemo(() => {
    try {
      const n = JSON.parse(sessionStorage.getItem("midtc_ticket_nav") || "null");
      if (!n || !Array.isArray(n.ids)) return null;
      const i = n.ids.indexOf(id);
      if (i < 0) return null;
      return { prev: n.ids[i - 1] || null, next: n.ids[i + 1] || null, pos: (n.page - 1) * (n.pageSize || 25) + i + 1, total: n.total };
    } catch { return null; }
  }, [id]);

  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100%", color:"var(--t-text-faint)" }}>
      <div className="animate-spin" style={{ width:28, height:28, border:"3px solid var(--t-accent)", borderTopColor:"transparent", borderRadius:"50%" }} />
    </div>
  );
  if (!ticket) return (
    <div style={{ padding:40, textAlign:"center", color:"var(--t-text-faint)" }}>
      {loadError === "failed"
        ? <>Couldn't load this ticket — the connection dropped or the server didn't answer in time. <button className="cv-link" onClick={() => setReloadKey(k => k + 1)}>Try again</button></>
        : <>Ticket not found. <button className="cv-link" onClick={() => navigate(listPath)}>Back to tickets</button></>}
    </div>
  );

  const requester = ticket.submitter_name || ticket.client_name || "Client";
  const sla = slaInfo(ticket);
  const assignees = [ticket.assigned_to_name, ticket.secondary_assigned_to_name].filter(Boolean);
  const teamsEmails = [ticket.assigned_to, ticket.secondary_assigned_to].map(x => agents.find(a => a.id === x)?.email).filter(e => e && e !== agent?.email);
  const teamsHref = teamsEmails.length ? `https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(teamsEmails.join(","))}&topicName=${encodeURIComponent(String(ticket.ticket_number || ""))}&message=${encodeURIComponent(`About ${ticket.ticket_number} — ${ticket.subject}\nhttps://ticketing.midtc.com/tickets/${ticket.id}`)}` : null;
  const ftrLine = ticket.ftr_disabled_at ? `FTR is off — ${FTR_OFF_REASONS[ticket.ftr_disabled_reason] || ticket.ftr_disabled_reason || "switched off"}` : "FTR is active on this ticket";
  const attachBytes = attachments.reduce((s, f) => s + (f.size || 0), 0);
  const elapsedMs = ticket.created_at ? Date.now() - new Date(ticket.created_at) : 0;
  const due = ticket.sla_due_at ? new Date(ticket.sla_due_at) : null;
  const slaPct = due && ticket.created_at ? Math.max(0, Math.min(100, Math.round(elapsedMs / (due - new Date(ticket.created_at)) * 100))) : null;
  const slaBar = sla.tone === "red" ? "linear-gradient(90deg,#f87171,#ef4444)" : sla.tone === "orange" ? "linear-gradient(90deg,#fbbf24,#f59e0b)" : "var(--t-accent-grad)";
  const chips = [["all", "All"], ["client", "Client"], ["replies", "Replies"], ["internal", "Internal"], ["activity", "Activity"]].map(([key, label]) => ({ key, label, count: counts[key] }));

  return (
    <div className="cv-root" style={{ display:"flex", flexDirection:"column", flex:1, minHeight:0, overflow: isMobile ? "auto" : "hidden" }}>
      <ConvoStyles />

      {/* ── Header ── */}
      <header style={{ padding: isMobile ? "14px 16px" : "16px 30px", borderBottom:"1px solid var(--t-border-soft)", display:"flex", flexDirection:"column", gap:12, background:"var(--t-gloss, none), transparent", flexShrink:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
          <button type="button" className="cv-link" onClick={() => navigate(listPath)} style={{ display:"inline-flex", alignItems:"center", gap:6, color:"var(--t-text-muted)", fontWeight:500 }}>
            <ArrowLeft size={15} /> Tickets
          </button>
          <span style={{ color:"var(--t-text-faint)" }}>/</span>
          <span style={{ fontWeight:700, color:"var(--t-accent)", fontSize:13 }}>{ticket.ticket_number}</span>
          {nav && (
            <span style={{ display:"inline-flex", alignItems:"center", gap:2, marginLeft:6 }}>
              <button type="button" className="cv-btn cv-btn-icon cv-btn-ghost" aria-label="Previous ticket" title="Previous ticket" disabled={!nav.prev} onClick={() => navigate(`${listPath}/${nav.prev}`)}><ChevronLeft size={16} /></button>
              <span style={{ fontSize:12, color:"var(--t-text-faint)" }}>{nav.pos} of {nav.total}</span>
              <button type="button" className="cv-btn cv-btn-icon cv-btn-ghost" aria-label="Next ticket" title="Next ticket" disabled={!nav.next} onClick={() => navigate(`${listPath}/${nav.next}`)}><ChevronRight size={16} /></button>
            </span>
          )}
          {updating && <span className="animate-spin" style={{ width:13, height:13, border:"2px solid var(--t-accent)", borderTopColor:"transparent", borderRadius:"50%", marginLeft:6 }} />}
          <div style={{ flex:1 }} />
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            <div style={{ position:"relative" }}>
              <button type="button" data-popover-trigger className={`cv-btn${remoteOpen ? " cv-btn-on" : ""}`} aria-expanded={remoteOpen} aria-haspopup="menu" onClick={() => setRemoteOpen(o => !o)}>
                <Monitor size={16} /><span className="cv-hide-sm">Remote</span><ChevronDown size={12} />
              </button>
              {/* Kept mounted while closed: a running remote session lives in these buttons. */}
              <Popover open={remoteOpen} onClose={() => setRemoteOpen(false)} keepMounted width={280}>
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  <div style={{ fontSize:12, color:"var(--t-text-faint)" }}>{ticket.device_name || ticket.remote_device_id ? `Device: ${ticket.device_name || ticket.remote_device_id}` : "No device linked to this ticket or its requester."}</div>
                  <RemoteButton mode="rustdesk" deviceId={ticket.remote_device_id || ticket.device_id || ""} deviceName={ticket.device_name || ticket.remote_device_id || "No device linked"} contactName={ticket.submitter_name || ticket.client_name} rustdeskId={ticket.rustdesk_id} fullWidth />
                  <RemoteButton mode="mesh" deviceId={ticket.remote_device_id || ticket.device_id || ""} deviceName={ticket.device_name || ticket.remote_device_id || "No device linked"} contactName={ticket.submitter_name || ticket.client_name} meshcentralNodeId={ticket.meshcentral_node_id} fullWidth />
                </div>
              </Popover>
            </div>
            <button type="button" className="cv-btn" onClick={() => setShowSecureLink(true)}><Lock size={16} /><span className="cv-hide-sm">Secure link</span></button>
            {canSendToQueue && <button type="button" className="cv-btn" onClick={() => setShowDev(true)}><Code size={16} /><span className="cv-hide-sm">Send to Dev</span></button>}
            {canSendToQueue && <button type="button" className="cv-btn" onClick={() => setShowInfra(true)}><Wrench size={16} /><span className="cv-hide-sm">Project</span></button>}
            {canSendToQueue && (ticket.scheduled_at ? (
              <button type="button" className="cv-btn cv-btn-on" title="Cancel the schedule and reopen the ticket" onClick={async () => {
                try { const res = await unscheduleTicket(ticket.id); setTicket(prev => ({ ...prev, ...res.data })); setToast({ message:"Schedule cancelled — ticket reopened", color:"#2563eb" }); }
                catch (e) { setToast({ message:e.response?.data?.error || "Failed to cancel schedule", color:"#dc2626" }); }
              }}>
                <CalendarClock size={16} /><span className="cv-hide-sm">Scheduled · Cancel</span>
              </button>
            ) : (
              <button type="button" className="cv-btn" onClick={() => setShowSchedule(true)}><CalendarClock size={16} /><span className="cv-hide-sm">Schedule</span></button>
            ))}
            <div style={{ position:"relative" }}>
              <button type="button" data-popover-trigger className={`cv-btn cv-btn-icon${moreOpen ? " cv-btn-on" : ""}`} aria-label="More actions" title="More actions" aria-haspopup="menu" aria-expanded={moreOpen} onClick={() => setMoreOpen(o => !o)}><MoreHorizontal size={16} /></button>
              <Popover open={moreOpen} onClose={() => setMoreOpen(false)} width={270}>
                {canSendToQueue && <button type="button" className="cv-menuitem" role="menuitem" onClick={() => { setMoreOpen(false); setShowRelaunchFtr(true); }}><Bot size={16} /> Relaunch FTR</button>}
                {teamsHref && <a className="cv-menuitem" role="menuitem" href={teamsHref} target="_blank" rel="noreferrer" onClick={() => setMoreOpen(false)}><MessageSquare size={16} /> Chat in Teams with the assigned tech</a>}
                {linkedDev && <button type="button" className="cv-menuitem" role="menuitem" onClick={() => go(`/dev-tickets/${linkedDev.id}`)}><Code size={16} /> Open the dev ticket</button>}
                {ticket.linked_project?.id && <button type="button" className="cv-menuitem" role="menuitem" onClick={() => go(`/projects?project=${ticket.linked_project.id}`)}><Wrench size={16} /> Open the project</button>}
                <div style={{ fontSize:11.5, color:"var(--t-text-faint)", padding:"8px 10px 2px", lineHeight:1.5 }}>{ftrLine}</div>
              </Popover>
            </div>
          </div>
        </div>

        <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
          <h1 style={{ fontSize: isMobile ? 19 : 22, lineHeight:1.3, overflowWrap:"anywhere" }}>{ticket.subject}</h1>
          <PillSel label="Status" value={ticket.status} tone={STATUS_TONE[ticket.status] || "gray"} options={["Open","In Progress","On Hold","Resolved","Closed"]} onChange={e => handleUpdate("status", e.target.value)} disabled={updating} />
          <PillSel label="Priority" value={ticket.priority} tone={PRIORITY_TONE[ticket.priority] || "gray"} options={["Critical","High","Medium","Low"]} onChange={e => handleUpdate("priority", e.target.value)} disabled={updating} />
          <span className={`cv-pill cv-c-${sla.tone}`}>{sla.text}</span>
          {ticket.scheduled_at && (
            <span className="cv-pill cv-c-blue"><CalendarClock size={13} /> Scheduled {new Date(ticket.scheduled_at).toLocaleString(undefined, { month:"short", day:"numeric", hour:"numeric", minute:"2-digit" })}</span>
          )}
        </div>

        <div style={{ display:"flex", alignItems:"center", gap:12, flexWrap:"wrap", fontSize:13 }}>
          <button type="button" className="cv-link" disabled={!ticket.contact_id} onClick={() => ticket.contact_id && go(`/contacts/${ticket.contact_id}`)}
            style={{ display:"inline-flex", alignItems:"center", gap:9, color:"var(--t-text-primary)", fontWeight:700, cursor: ticket.contact_id ? "pointer" : "default" }} title={ticket.contact_id ? "Open the contact" : undefined}>
            <Avatar name={requester} kind="client" size={26} /> {requester}
          </button>
          {ticket.client_name && (
            <button type="button" className="cv-link" disabled={!ticket.client_id} onClick={() => ticket.client_id && go(`/clients/${ticket.client_id}`)} style={{ color:"var(--t-text-muted)", fontWeight:500, display:"inline-flex", alignItems:"center", gap:5 }}>
              <Building2 size={13} /> {ticket.client_name}
            </button>
          )}
          {ticket.submitter_email && <span style={{ color:"var(--t-text-faint)" }}>{ticket.submitter_email}</span>}
          <span style={{ color:"var(--t-text-faint)" }}>· Opened {fmtFull(ticket.created_at)}</span>
          <div style={{ flex:1 }} />
          <span style={{ color:"var(--t-text-faint)" }}>{assignees.length ? "Assigned" : "Unassigned"}</span>
          {assignees.length > 0 && (
            <span style={{ display:"inline-flex", alignItems:"center", gap:6 }} title={assignees.join(" + ")}>
              {assignees.map(n => <MiniAvatar key={n} name={n} size={26} />)}
              <span className="cv-hide-sm" style={{ color:"var(--t-text-muted)" }}>{assignees.map(firstName).join(" + ")}</span>
            </span>
          )}
          {watchers.length > 0 && (
            <span role="status" style={{ display:"inline-flex", alignItems:"center", gap:6 }}
              title={`${watchers.map(w => w.name).join(", ")} ${watchers.length === 1 ? "is" : "are"} viewing this ticket right now`}>
              <span style={{ color:"var(--t-text-faint)", display:"inline-flex", alignItems:"center", gap:5 }}>
                <Eye size={14} aria-hidden="true" style={{ color:"var(--badge-green)" }} />
                {watchers.length === 1 ? "Watching" : `${watchers.length} watching`}
              </span>
              {watchers.map(w => <MiniAvatar key={w.id} name={w.name} size={26} />)}
              <span className="cv-hide-sm" style={{ color:"var(--t-text-muted)" }}>{watchers.map(w => firstName(w.name)).join(" + ")}</span>
            </span>
          )}
        </div>
      </header>

      {/* ── Body: conversation + details ── */}
      <div style={{ flex: isMobile ? "none" : 1, minHeight:0, display:"flex", flexDirection: isMobile ? "column" : "row" }}>
        <section aria-label="Conversation" style={{ flex: isMobile ? "none" : 1, minWidth:0, minHeight:0, display:"flex", flexDirection:"column" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap", padding: isMobile ? "10px 16px" : "12px 30px", borderBottom:"1px solid var(--t-border-softer)", flexShrink:0 }}>
            <Chips items={chips} value={filter} onChange={setFilter} />
            <div style={{ flex:1 }} />
            <label className="cv-input" style={{ display:"flex", alignItems:"center", gap:8, height:36, padding:"0 12px", width: isMobile ? "100%" : 230 }}>
              <Search size={15} aria-hidden="true" style={{ color:"var(--t-text-faint)" }} />
              <input type="search" value={search} onChange={e => setSearch(e.target.value)} aria-label="Search this conversation" placeholder="Search this conversation"
                style={{ border:0, background:"transparent", color:"inherit", fontFamily:"inherit", fontSize:13, outline:"none", width:"100%" }} />
            </label>
            <button type="button" className="cv-btn cv-btn-ghost" onClick={allOpen ? collapseAll : expandAll}>{allOpen ? "Collapse all" : "Expand all"}</button>
          </div>

          <div style={{ position:"relative", flex: isMobile ? "none" : 1, minHeight:0, display:"flex", flexDirection:"column" }}>
            <div ref={scrollRef} className="cv-scroll" style={{ flex: isMobile ? "none" : 1, minHeight: isMobile ? "auto" : 0, overflowY: isMobile ? "visible" : "auto", padding: isMobile ? "16px 12px 24px" : "22px 30px 30px" }}>
              <div style={{ maxWidth:960, margin:"0 auto", display:"flex", flexDirection:"column", gap:12 }}>
                {visible.length === 0 && (
                  <div style={{ textAlign:"center", color:"var(--t-text-faint)", fontSize:13, padding:32 }}>
                    {search ? `Nothing in this conversation matches “${search}”.` : "Nothing to show for this filter."}
                  </div>
                )}
                <Timeline entries={visible} expanded={expanded} toggle={toggle} focusedId={focusId} refFor={refFor}
                  onReply={replyFrom} />
                <KeyHints items={[[["J","K"], "next / previous"], [["Enter"], "open"], [["R"], "reply"], [["N"], "internal note"], [["E"], "expand all"]]} />
              </div>
            </div>
            <JumpLatest scrollRef={scrollRef} bump={entries.length} />
          </div>

          {/* ── Composer ── */}
          {!composeOpen ? (
            <div className="cv-compose" style={{ padding: isMobile ? "12px 14px" : "14px 30px", display:"flex", alignItems:"center", gap:12, flexWrap:"wrap", flexShrink:0 }}>
              <Avatar name={`${agent?.firstName || agent?.first_name || ""} ${agent?.lastName || agent?.last_name || ""}`} kind="tech" />
              <button type="button" className="cv-input" onClick={() => openComposer("reply")} style={{ flex:1, minWidth:200, height:44, textAlign:"left", padding:"0 16px", color:"var(--t-text-faint)", fontSize:14, cursor:"text" }}>
                {reply ? `Draft: ${previewText(reply, 70)}` : `Reply to ${firstName(requester) || requester}…`}
              </button>
              <button type="button" className="cv-btn" onClick={() => openComposer("note")}><Lock size={15} /> Internal note</button>
              <button type="button" className="cv-btn" onClick={handleSuggest}><Sparkles size={15} /> AI suggest</button>
            </div>
          ) : (
            <div className="cv-compose" style={{ padding: isMobile ? "14px" : "16px 30px 18px", display:"flex", flexDirection:"column", gap:12, flexShrink:0, maxHeight: isMobile ? "none" : "58vh", overflowY:"auto" }}>
              <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                <button type="button" className={`cv-chip${!isNote ? " on" : ""}`} onClick={() => setMode("reply")}>Reply to {firstName(requester) || requester}</button>
                <button type="button" className={`cv-chip cv-chip-note${isNote ? " on" : ""}`} onClick={() => setMode("note")}><Lock size={13} /> Internal note</button>
                <div style={{ flex:1 }} />
                {isNote
                  ? <span style={{ fontSize:12, color:"var(--badge-amber)", fontWeight:700 }}>Only MIDTC staff can see this</span>
                  : !ticket.contact_id && <span style={{ fontSize:12, color:"var(--badge-orange)", fontWeight:700 }}>No client contact on this ticket — the reply is saved, not emailed</span>}
                <button type="button" className="cv-btn cv-btn-icon cv-btn-ghost" aria-label="Minimise the reply box" title="Minimise (your text is kept)" onClick={() => setComposeOpen(false)}><ChevronDown size={16} /></button>
              </div>

              {!isNote && ticket.contact_id && (
                <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", fontSize:12.5 }}>
                  <span style={{ color:"var(--t-text-faint)" }}>To</span>
                  <span className="cv-ccchip" style={{ paddingRight:10 }}>{requester}{ticket.submitter_email ? ` <${ticket.submitter_email}>` : ""}</span>
                  <span style={{ color:"var(--t-text-faint)", marginLeft:4 }}>Cc</span>
                  {ccList.length === 0 && ccAdd.length === 0 && <span style={{ color:"var(--t-text-faint)" }}>nobody</span>}
                  {ccList.map(e => <span key={e} className="cv-ccchip" style={{ paddingRight:10 }} title={ccOrigin(ticket, e)}>{e}</span>)}
                  {ccAdd.map(e => (
                    <span key={e} className="cv-ccchip cv-ccchip-new" title="Added to CC when you send">{e}
                      <button type="button" aria-label={`Don't add ${e}`} onClick={() => setCcAdd(p => p.filter(x => x !== e))}><X size={12} /></button>
                    </span>
                  ))}
                  {ccEditing ? (
                    <span className="cv-input" style={{ display:"inline-flex", alignItems:"center", height:28, padding:"0 8px", borderRadius:999 }}>
                      <CcSuggest autoFocus clientId={ticket.client_id} value={ccDraft} onChange={e => setCcDraft(e.target.value)} placeholder="name@company.com"
                        onPick={email => addPendingCc(email)} exclude={[...ccList, ...ccAdd, ticket.submitter_email]}
                        onKeyDown={e => { if (e.key === "Enter" || e.key === "," ) { e.preventDefault(); addPendingCc(); } if (e.key === "Escape") { setCcEditing(false); setCcDraft(""); } }}
                        style={{ border:0, background:"transparent", color:"inherit", fontFamily:"inherit", fontSize:12.5, outline:"none", width:190 }} aria-label="Add someone to Cc" />
                    </span>
                  ) : (
                    <button type="button" className="cv-link" style={{ fontSize:12.5 }} onClick={() => setCcEditing(true)}>+ Add Cc</button>
                  )}
                </div>
              )}

              {aiSuggest.visible && (
                <div style={{ background:"var(--t-accent-softer)", border:"1px solid var(--t-accent-soft)", borderRadius:"var(--t-radius-md,12px)", padding:"10px 12px" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom: aiSuggest.loading ? 0 : 4 }}>
                    <Sparkles size={14} style={{ color:"var(--t-accent)" }} />
                    <span style={{ fontSize:12, fontWeight:700, color:"var(--t-text-primary)" }}>{aiSuggest.loading ? "Writing a suggested reply…" : "Suggested reply — it's in the box below to edit"}</span>
                    <button type="button" aria-label="Dismiss the suggestion" onClick={() => setAiSuggest({ visible:false, loading:false, text:"" })} style={{ marginLeft:"auto", background:"none", border:0, color:"var(--t-text-faint)", cursor:"pointer", display:"inline-flex" }}><X size={14} /></button>
                  </div>
                  {!aiSuggest.loading && aiSuggest.text.startsWith("Couldn't") && <div style={{ fontSize:12, color:"var(--badge-red)" }}>{aiSuggest.text}</div>}
                </div>
              )}

              <div className="cv-grow">
              <TopResizeHandle targetRef={textRef} min={120} />
              <textarea ref={textRef} className="cv-input" rows={5} value={reply} onChange={e => setReply(e.target.value)} onPaste={handleReplyPaste}
                onKeyDown={e => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); handleSend(); } if (e.key === "Escape") setComposeOpen(false); }}
                aria-label={isNote ? "Internal note" : `Reply to ${requester}`}
                placeholder={isNote ? "Add a note for the team. The client never sees it. Ctrl+Enter adds it." : "Write your reply. Paste screenshots straight in. Ctrl+Enter sends."}
                style={{ resize:"vertical", padding:"13px 15px", fontSize:14, lineHeight:1.6, minHeight:120,
                  ...(isNote ? { background:"var(--t-note-bg)", borderColor:"var(--t-note-bdr)" } : {}) }} />
              </div>

              {!isNote && agentSignature && (
                <div style={{ fontSize:12, color:"var(--t-text-faint)" }}>
                  <button type="button" className="cv-link" style={{ fontSize:12 }} onClick={() => setShowSig(s => !s)}>{showSig ? "Hide signature" : "Your signature is added"}</button>
                  {showSig && <div style={{ marginTop:8, padding:"10px 12px", borderRadius:8, background:"#fff", color:"#374151", fontSize:13 }} dangerouslySetInnerHTML={sigHtml} />}
                </div>
              )}

              {attachments.length > 0 && (
                <div style={{ display:"flex", flexWrap:"wrap", gap:8, alignItems:"center" }}>
                  {attachments.map((f, i) => (
                    <span key={`${f.name}-${i}`} className="cv-ccchip">
                      <Paperclip size={12} /> <span style={{ maxWidth:220, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{f.name}</span>
                      <span style={{ color:"var(--t-text-faint)" }}>{fmtFileSize(f.size)}</span>
                      <button type="button" aria-label={`Remove ${f.name}`} onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))}><X size={12} /></button>
                    </span>
                  ))}
                  <span style={{ fontSize:12, color:"var(--t-text-faint)" }}>{attachments.length} file{attachments.length > 1 ? "s" : ""} · {fmtFileSize(attachBytes)}</span>
                </div>
              )}
              {uploadPct !== null && (
                <div aria-live="polite">
                  <div className="cv-progress"><div style={{ width:`${uploadPct}%` }} /></div>
                  <div style={{ fontSize:12, color:"var(--t-text-faint)", marginTop:4 }}>Uploading… {uploadPct}%</div>
                </div>
              )}
              {sendError && <div role="alert" style={{ fontSize:12.5, color:"var(--badge-red)" }}>{sendError}</div>}

              <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                <input ref={fileInputRef} type="file" multiple style={{ display:"none" }} onChange={e => { addFilesToReply(Array.from(e.target.files || [])); e.target.value = ""; }} />
                <button type="button" className="cv-btn cv-btn-icon cv-btn-ghost" aria-label="Bold" title="Bold" onClick={() => wrapSelection("**")}><Bold size={16} /></button>
                <button type="button" className="cv-btn cv-btn-icon cv-btn-ghost" aria-label="Inline code" title="Inline code" onClick={() => wrapSelection("`")}><Code size={16} /></button>
                <button type="button" className="cv-btn cv-btn-icon cv-btn-ghost" aria-label="Attach files" title="Attach files (or paste a screenshot)" onClick={() => fileInputRef.current?.click()}><Paperclip size={16} /></button>
                {!isNote && <button type="button" className="cv-btn cv-btn-icon cv-btn-ghost" aria-label="Send a secure link" title="Send a secure link" onClick={() => setShowSecureLink(true)}><Lock size={16} /></button>}
                <div style={{ flex:1 }} />
                {!isNote && <button type="button" className="cv-btn" onClick={handleSuggest} disabled={aiSuggest.loading}><Sparkles size={15} /> AI suggest</button>}
                <button type="button" className="cv-btn cv-btn-primary" onClick={handleSend} disabled={sending || !(reply.trim() || attachments.length)}>
                  <Send size={15} /> {sending ? (isNote ? "Adding…" : "Sending…") : isNote ? "Add note" : "Send"}
                </button>
              </div>
            </div>
          )}
        </section>

        {/* ── Details ── */}
        <aside aria-label="Ticket details" style={{ width: isMobile ? "100%" : 350, flexShrink:0, minHeight:0, overflowY: isMobile ? "visible" : "auto", borderLeft: isMobile ? "none" : "1px solid var(--t-border-soft)", borderTop: isMobile ? "1px solid var(--t-border-soft)" : "none", background:"var(--t-gloss, none), var(--t-panel)" }}>
          <div className="cv-sect">
            <span className="cv-lbl">Ticket</span>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(2, minmax(0, 1fr))", gap:12 }}>
              {[["Status","status",["Open","In Progress","On Hold","Resolved","Closed"]],["Priority","priority",["Critical","High","Medium","Low"]],["Category","category",["General","Email","Network","Hardware","Software","Security","Onboarding","Performance","Other"]]].map(([label, field, opts]) => (
                <label key={field} style={{ display:"flex", flexDirection:"column", gap:6, minWidth:0 }}>
                  <span style={{ fontSize:12, color:"var(--t-text-faint)" }}>{label}</span>
                  <Sel label={label} value={ticket[field] || ""} disabled={updating} onChange={e => handleUpdate(field, e.target.value)}>
                    {!opts.includes(ticket[field]) && ticket[field] && <option value={ticket[field]}>{ticket[field]}</option>}
                    {opts.map(o => <option key={o} value={o}>{o}</option>)}
                  </Sel>
                </label>
              ))}
              <label style={{ display:"flex", flexDirection:"column", gap:6, minWidth:0 }}>
                <span style={{ fontSize:12, color:"var(--t-text-faint)" }}>SLA</span>
                <Sel label="SLA" value={ticket.sla_status || "ok"} disabled={updating} onChange={e => handleUpdate("sla_status", e.target.value)}>
                  <option value="ok">In SLA</option><option value="hold">On Hold</option><option value="breach">Breach</option>
                </Sel>
              </label>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:7, marginTop:2 }}>
              <div className="cv-kv" style={{ fontSize:12 }}><span>{due ? `Due ${fmtFull(ticket.sla_due_at)}` : "No SLA deadline set"}</span><span style={{ color:"var(--t-text-muted)" }}>{ticket.sla_policy_name || ""}</span></div>
              {slaPct !== null && <div className="cv-bar"><div style={{ width:`${slaPct}%`, height:"100%", background:slaBar }} /></div>}
              <div className="cv-kv" style={{ fontSize:12 }}><span>Open for</span><span style={{ color:"var(--t-text-muted)" }}>{Math.floor(elapsedMs / 3600000)}h {Math.floor((elapsedMs % 3600000) / 60000)}m</span></div>
            </div>
          </div>

          <div className="cv-sect">
            <span className="cv-lbl">People</span>
            <div style={{ display:"flex", alignItems:"center", gap:12 }}>
              <Avatar name={requester} kind="client" />
              <div style={{ display:"flex", flexDirection:"column", gap:2, minWidth:0 }}>
                {ticket.contact_id
                  ? <button type="button" className="cv-link" style={{ color:"var(--t-text-primary)", fontSize:14, fontWeight:700, textAlign:"left" }} onClick={() => go(`/contacts/${ticket.contact_id}`)}>{requester}</button>
                  : <strong style={{ color:"var(--t-text-primary)", fontSize:14 }}>{requester}</strong>}
                <span style={{ fontSize:12, color:"var(--t-text-faint)" }}>{[ticket.submitter_title, ticket.client_name].filter(Boolean).join(" · ")}{ticket.client_tier ? ` · ${ticket.client_tier}` : ""}</span>
                {ticket.submitter_email && <a href={`mailto:${ticket.submitter_email}`} style={{ fontSize:13, color:"var(--t-accent)", overflowWrap:"anywhere" }}>{ticket.submitter_email}</a>}
                {ticket.submitter_phone && <span style={{ fontSize:12, color:"var(--t-text-muted)" }}>{ticket.submitter_phone}</span>}
              </div>
            </div>

            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              <div style={{ display:"flex", alignItems:"center", gap:6 }}><Users size={14} style={{ color:"var(--t-text-faint)" }} /><span style={{ fontSize:12, color:"var(--t-text-faint)" }}>Copied on replies (Cc)</span></div>
              {ccList.length === 0 && <span style={{ fontSize:12.5, color:"var(--t-text-faint)" }}>Nobody else is copied.</span>}
              {ccList.map(e => (
                <div key={e} style={{ display:"flex", alignItems:"flex-start", gap:8, justifyContent:"space-between" }}>
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontSize:13, color:"var(--t-text-body)", overflowWrap:"anywhere" }}>{e}</div>
                    <div style={{ fontSize:11.5, color:"var(--t-text-faint)" }}>{ccOrigin(ticket, e)}</div>
                  </div>
                  <button type="button" className="cv-btn cv-btn-icon cv-btn-ghost" style={{ width:28, height:28 }} aria-label={`Remove ${e} from Cc`} title="Remove from Cc" disabled={ccSaving} onClick={() => removeCc(e)}><X size={14} /></button>
                </div>
              ))}
              <div style={{ display:"flex", gap:6 }}>
                <CcSuggest clientId={ticket.client_id} value={ccInput} onChange={e => { setCcInput(e.target.value); setCcError(""); }} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addCc(); } }}
                  onPick={email => addCc(email)} exclude={[...ccList, ticket.submitter_email]} wrapStyle={{ flex:1 }}
                  placeholder="Add someone: name@company.com" aria-label="Add someone to Cc" className="cv-input"
                  style={{ flex:1, minWidth:0, height:36, padding:"0 11px", fontSize:13, borderRadius:"var(--t-radius-sm,8px)" }} />
                <button type="button" className="cv-btn" disabled={ccSaving || !ccInput.trim()} onClick={() => addCc()}><Plus size={14} /> Add</button>
              </div>
              {ccError && <div style={{ fontSize:12, color:"var(--badge-red)" }}>{ccError}</div>}
              {(ticket.cc_log || []).length > 0 && (
                <div>
                  <button type="button" className="cv-link" style={{ fontSize:12, display:"inline-flex", alignItems:"center", gap:5 }} onClick={() => setCcHistory(o => !o)}><History size={13} /> {ccHistory ? "Hide CC history" : `CC history (${ticket.cc_log.length})`}</button>
                  {ccHistory && (
                    <ul style={{ margin:"8px 0 0", paddingLeft:16, display:"flex", flexDirection:"column", gap:4 }}>
                      {[...ticket.cc_log].reverse().map((x, i) => <li key={i} style={{ fontSize:12, color:"var(--t-text-muted)" }}>{ccHistoryLine(x)}</li>)}
                    </ul>
                  )}
                </div>
              )}
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"repeat(2, minmax(0, 1fr))", gap:12 }}>
              <label style={{ display:"flex", flexDirection:"column", gap:6, minWidth:0 }}>
                <span style={{ fontSize:12, color:"var(--t-text-faint)" }}>Primary tech</span>
                <Sel label="Primary tech" value={ticket.assigned_to || ""} disabled={updating} onChange={e => handleUpdate("assigned_to", e.target.value)}>
                  <option value="">Unassigned</option>
                  {agents.map(a => <option key={a.id} value={a.id}>{a.first_name} {a.last_name}</option>)}
                </Sel>
              </label>
              <label style={{ display:"flex", flexDirection:"column", gap:6, minWidth:0 }}>
                <span style={{ fontSize:12, color:"var(--t-text-faint)" }}>Secondary tech</span>
                <Sel label="Secondary tech" value={ticket.secondary_assigned_to || ""} disabled={updating} onChange={e => handleUpdate("secondary_assigned_to", e.target.value)}>
                  <option value="">None</option>
                  {agents.filter(a => a.id !== ticket.assigned_to).map(a => <option key={a.id} value={a.id}>{a.first_name} {a.last_name}</option>)}
                </Sel>
              </label>
            </div>
            {teamsHref && <a className="cv-btn" href={teamsHref} target="_blank" rel="noreferrer" style={{ alignSelf:"flex-start" }}><MessageSquare size={15} /> Chat in Teams</a>}
          </div>

          <div className="cv-sect">
            <span className="cv-lbl">Time</span>
            <TimeTracker entityType="ticket" entityId={ticket.id} />
          </div>

          <div className="cv-sect">
            <span className="cv-lbl">Linked</span>
            <div className="cv-kv"><span>Project</span>{ticket.linked_project?.id
              ? <button type="button" className="cv-link" style={{ textAlign:"right", maxWidth:200, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }} title={ticket.linked_project.title} onClick={() => go(`/projects?project=${ticket.linked_project.id}`)}>{ticket.linked_project.title}</button>
              : <span style={{ color:"var(--t-text-muted)" }}>None</span>}</div>
            {canSeeDevQueue && <div className="cv-kv"><span>Dev ticket</span>{linkedDev
              ? <button type="button" className="cv-link" style={{ textAlign:"right", maxWidth:200, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }} title={linkedDev.title} onClick={() => go(`/dev-tickets/${linkedDev.id}`)}>{linkedDev.status} · {linkedDev.title}</button>
              : <span style={{ color:"var(--t-text-muted)" }}>None</span>}</div>}
            <div className="cv-kv"><span>Device</span><span style={{ color:"var(--t-text-muted)", textAlign:"right" }}>{ticket.device_name || "None"}{ticket.remote_device_inferred ? " (requester's)" : ""}</span></div>
            <div className="cv-kv"><span>FTR</span><span style={{ color:"var(--t-text-muted)", textAlign:"right", maxWidth:220 }}>{ticket.ftr_disabled_at ? "Off" : "Active"}</span></div>
          </div>

          {(ticket.tags || []).length > 0 && (
            <div className="cv-sect">
              <span className="cv-lbl">Tags</span>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                {ticket.tags.map(tg => <TagPill key={tg.id || tg.name} name={tg.name} color={tg.color} size="md" onClick={(name) => { try { const f = JSON.parse(localStorage.getItem("midtc_ticket_filters") || "{}"); localStorage.setItem("midtc_ticket_filters", JSON.stringify({ ...f, tag: name })); } catch { /* ignore */ } navigate(listPath); }} />)}
              </div>
            </div>
          )}

          <div className="cv-sect">
            <span className="cv-lbl">Dates</span>
            {[["Opened", ticket.created_at], ["First response", ticket.first_response_at], ["Last activity", ticket.last_activity_at], ["Resolved", ticket.resolved_at], ["Closed", ticket.closed_at]].filter(([, v]) => v).map(([label, v]) => (
              <div key={label} className="cv-kv"><span>{label}</span><span style={{ color:"var(--t-text-muted)", textAlign:"right" }}>{fmtFull(v)}</span></div>
            ))}
          </div>

          <div className="cv-sect">
            <span className="cv-lbl">Private notes</span>
            <textarea key={ticket.id} className="cv-input" defaultValue={ticket.internal_notes || ""} onBlur={e => { if ((e.target.value || "") !== (ticket.internal_notes || "")) handleUpdate("internal_notes", e.target.value); }}
              placeholder="Notes only staff see — saved when you click away." rows={4} aria-label="Private notes"
              style={{ resize:"vertical", padding:"10px 12px", fontSize:13, lineHeight:1.6, borderRadius:"var(--t-radius-sm,8px)" }} />
          </div>

          {(["Resolved", "Closed"].includes(ticket.status) || ticket.ai_summary) && (
            <div className="cv-sect" style={{ borderBottom:0 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <span className="cv-lbl" style={{ display:"inline-flex", alignItems:"center", gap:6 }}><Sparkles size={12} /> AI summary</span>
                <button type="button" className="cv-link" disabled={generatingSummary} onClick={handleGenerateSummary}>{generatingSummary ? "Writing…" : ticket.ai_summary ? "Refresh" : "Generate"}</button>
              </div>
              {ticket.ai_summary
                ? <p style={{ margin:0, fontSize:13, lineHeight:1.65, color:"var(--t-text-muted)", whiteSpace:"pre-wrap" }}>{ticket.ai_summary}</p>
                : <p style={{ margin:0, fontSize:12.5, color:"var(--t-text-faint)", fontStyle:"italic" }}>{ticket.status === "Resolved" ? "Written automatically when the ticket closes, or click Generate." : "Click Generate to write one."}</p>}
            </div>
          )}
        </aside>
      </div>

      {/* Modals */}
      {showDev && (
        <SendToDevModal ticket={ticket} agents={agents} onClose={() => setShowDev(false)}
          onSent={(data) => {
            setShowDev(false);
            setToast({ message:`Sent to the dev queue — ${data.assigned_devs?.map(d=>`${d.first_name} ${d.last_name}`).join(", ")} notified`, color:"#7c3aed" });
            loadAll().catch(() => {});
            setLinkedDev(null);
          }} />
      )}
      {showInfra && (
        <SendToInfraModal ticket={ticket} agents={agents} onClose={() => setShowInfra(false)}
          onSent={() => { setShowInfra(false); setToast({ message:"Project created — team notified", color:"#0891b2" }); loadAll().catch(() => {}); }} />
      )}
      {showSecureLink && (
        <SecureLinkModal ticketId={ticket.id} defaultEmail={ticket.submitter_email || ""} defaultTitle={ticket.subject ? `Re: ${ticket.subject}` : ""} onClose={() => setShowSecureLink(false)} />
      )}
      {showRelaunchFtr && (
        <RelaunchFtrModal ticket={ticket} onClose={() => setShowRelaunchFtr(false)}
          onLaunched={(data) => {
            setShowRelaunchFtr(false);
            setToast({ message: data.warnings?.length ? `FTR relaunched — ${data.warnings[0]}` : "FTR relaunched — it's reading the ticket now; its reply lands in the thread shortly", color: data.warnings?.length ? "#d97706" : "#0e7490" });
            loadAll().catch(() => {});
          }} />
      )}
      {showSchedule && (
        <ScheduleModal ticket={ticket} onClose={() => setShowSchedule(false)}
          onScheduled={(data) => { setShowSchedule(false); setTicket(prev => ({ ...prev, ...data })); setToast({ message:"Ticket scheduled — reminders set", color:"#2563eb" }); }} />
      )}
      {toast && <Toast message={toast.message} color={toast.color} onDismiss={() => setToast(null)} />}
    </div>
  );
}
